import React, { useCallback, useEffect, useRef } from "react";
import SheetGround from "../shared/ui/SheetGround";
import { AcaIcon } from "../shared/aca";
import { VELLUM as T } from "../shared/folio/kit";
import { FP, folioFormCss } from "../shared/folio/form";

/* A sheet, lifted off the page.

   The dialog is drawn on the same ruled ground as the rest of the site and
   framed by the folio's heavy rule, so it reads as a sheet laid over the one
   behind it rather than as a different material. The shadow is the only
   depth in the system, and it is here because a modal that does not lift is
   a modal the eye cannot separate from the page.

   One authored moment: the sheet settles into place. It starts already
   legible and travels 10px, so nothing is hidden waiting on an animation and
   reduced motion simply lands on the resting state. */

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /* lg is for a sheet that carries a record rather than a form - the
     candidate dossier, where two columns of particulars have to sit side
     by side to be read as a pair. */
  size?: "sm" | "md" | "lg";
  /* Id of the heading inside `children` that names this dialog. */
  labelledBy?: string;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  children,
  size = "sm",
  labelledBy,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const returnFocusTo = useRef<HTMLElement | null>(null);

  /* Tab must not walk out of an open dialog and into the page behind it. */
  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab" || !panelRef.current) return;
      const items = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );
      if (items.length === 0) {
        e.preventDefault();
        panelRef.current.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === panelRef.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (!isOpen) return;

    returnFocusTo.current = document.activeElement as HTMLElement | null;
    document.addEventListener("keydown", onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    /* Into the dialog, but onto the sheet rather than the first field, so a
       screen reader hears what this is before what to type into it. */
    const id = window.setTimeout(() => panelRef.current?.focus(), 0);

    return () => {
      window.clearTimeout(id);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      returnFocusTo.current?.focus?.();
    };
  }, [isOpen, onKeyDown]);

  if (!isOpen) return null;

  return (
    <div
      className={`${FP}-scrim`}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        background: "rgba(12, 22, 20, 0.58)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        overflowY: "auto",
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <SheetGround
        className={`${FP}-sheet`}
        style={{
          position: "relative",
          color: T.ink,
          width: "100%",
          maxWidth: size === "lg" ? 880 : size === "md" ? 580 : 480,
          maxHeight: "90vh",
          overflowY: "auto",
          border: `1.5px solid ${T.ink}`,
          boxShadow: "0 26px 70px -12px rgba(12, 22, 20, 0.45)",
        }}
      >
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={labelledBy}
          tabIndex={-1}
          style={{ outline: "none" }}
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className={`${FP}-close`}
            style={{
              position: "absolute",
              top: 14,
              right: 14,
              width: 30,
              height: 30,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: T.mute,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
            }}
          >
            <AcaIcon name="close" size={16} />
          </button>
          {children}
        </div>
      </SheetGround>

      <style>{`
        ${folioFormCss}
        .${FP}-close:hover { color: ${T.ink}; }

        @media (prefers-reduced-motion: no-preference) {
          @keyframes ${FP}-scrim-in { from { opacity: 0; } to { opacity: 1; } }
          @keyframes ${FP}-sheet-in {
            from { opacity: 0; transform: translateY(10px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          .${FP}-scrim { animation: ${FP}-scrim-in 180ms linear both; }
          .${FP}-sheet { animation: ${FP}-sheet-in 280ms cubic-bezier(0.16, 1, 0.3, 1) both; }
        }
      `}</style>
    </div>
  );
};
